/** Показ полноэкранного входящего звонка без циклических импортов React ↔ WS/notifications. */
import { Platform } from 'react-native';
import { GROUP_CALLS_ENABLED } from '../config/featureFlags';
import { getActiveCall } from './activeCallRegistry';
import { incomingCallHeadline } from './callLabels';
import { decideIncomingInviteWhileBusy } from './callBusyRules';
import { clearRingingIncomingCall, noteIncomingCallRinging } from './ringingIncomingCallRegistry';

export type IncomingCallUIPayload = {
  chatId: string;
  callerName: string;
  callMode: 'audio' | 'video';
  callScope: 'direct' | 'group';
  /**
   * `callInviteId` звонка. Обязателен для групповых звонков: из него считается имя
   * комнаты LiveKit, и без него принимающий уходит в mesh, а звонящий отбрасывает
   * его `accept`. См. docs/stories/5.1-group-calls-bind-invite-leg.md.
   */
  inviteId?: string;
  callerUserId?: string;
  callerHasAvatar?: boolean;
  callerAvatarRevision?: number;
  callerAvatarShape?: string | null;
  /** Название группы: в групповом входящем заголовком идёт оно, а не звонящий (E2E-8). */
  groupTitle?: string;
};

/**
 * Параметры маршрута `Call` из payload входящего звонка.
 *
 * Вынесено из оверлея, чтобы «нога» (`callInviteId`) проверялась тестом: без неё
 * групповой звонок молча уходит в mesh и не соединяется (стори 5.1).
 */
export function callRouteParamsFromIncomingPayload(payload: IncomingCallUIPayload): {
  chatId: string;
  title: string;
  callerName?: string;
  incoming: true;
  callMode: 'audio' | 'video';
  callScope: 'direct' | 'group';
  callInviteId?: string;
  autoAccept: true;
  remoteUserId?: string;
  remoteHasAvatar?: boolean;
  remoteAvatarRevision?: number;
  remoteAvatarShape?: string | null;
} {
  const headline = incomingCallHeadline(payload);
  return {
    chatId: payload.chatId,
    title: headline.title,
    ...(headline.callerName ? { callerName: headline.callerName } : {}),
    incoming: true,
    callMode: payload.callMode,
    callScope: payload.callScope,
    ...(payload.inviteId ? { callInviteId: payload.inviteId } : {}),
    autoAccept: true,
    ...(payload.callerUserId
      ? {
          remoteUserId: payload.callerUserId,
          remoteHasAvatar: payload.callerHasAvatar,
          remoteAvatarRevision: payload.callerAvatarRevision ?? 0,
          remoteAvatarShape: payload.callerAvatarShape ?? null,
        }
      : {}),
  };
}

/** Поля аватара звонящего из объекта WS SIGNAL (сервер кладёт from* на envelope). */
export function callerAvatarFromWsSignal(data: Record<string, unknown>): {
  callerUserId?: string;
  callerHasAvatar?: boolean;
  callerAvatarRevision?: number;
  callerAvatarShape?: string | null;
} {
  const uid = data.fromUserId != null ? String(data.fromUserId) : '';
  if (!uid) return {};
  const hasRaw = data.fromHasAvatar;
  const callerHasAvatar =
    hasRaw === true || hasRaw === 'true' || hasRaw === 1 || hasRaw === '1';
  const revRaw = data.fromAvatarRevision;
  const callerAvatarRevision =
    typeof revRaw === 'number' && Number.isFinite(revRaw) ? revRaw : Number(revRaw) || 0;
  const shapeRaw = data.fromAvatarShape;
  const callerAvatarShape =
    shapeRaw != null && String(shapeRaw).length > 0 ? String(shapeRaw) : null;
  return { callerUserId: uid, callerHasAvatar, callerAvatarRevision, callerAvatarShape };
}

/** Поля аватара из FCM data-only (callerHasAvatar как "0"/"1"). */
export function callerAvatarFromPushData(data: Record<string, unknown>): {
  callerUserId?: string;
  callerHasAvatar?: boolean;
  callerAvatarRevision?: number;
  callerAvatarShape?: string | null;
} {
  const uidRaw = data.callerId;
  const callerUserId = typeof uidRaw === 'string' && uidRaw.length > 0 ? uidRaw : undefined;
  const hasRaw = data.callerHasAvatar;
  const callerHasAvatar =
    hasRaw === '1' || hasRaw === 1 || hasRaw === true || hasRaw === 'true';
  const revRaw = data.callerAvatarRevision;
  const callerAvatarRevision =
    typeof revRaw === 'number' && Number.isFinite(revRaw) ? revRaw : Number(revRaw) || 0;
  const shapeRaw = data.callerAvatarShape;
  const callerAvatarShape =
    typeof shapeRaw === 'string' && shapeRaw.length > 0 ? shapeRaw : null;
  if (!callerUserId) {
    return {};
  }
  return { callerUserId, callerHasAvatar, callerAvatarRevision, callerAvatarShape };
}

type Listener = (payload: IncomingCallUIPayload | null) => void;

let listener: Listener | null = null;
/**
 * Буфер на случай гонки холодного старта: notifee/getInitialNotification может вызвать
 * presentIncomingCallUI() раньше, чем IncomingCallOverlay смонтировался и подписался.
 * Без буфера payload терялся, и на Android после тапа по полноэкранному уведомлению
 * оверлей не показывался. Держим последний непоказанный payload и отдаём его при подписке.
 */
let pendingPayload: IncomingCallUIPayload | null = null;

/**
 * iOS показывает входящий через CallKit (APNs VoIP) — JS-оверлей там не нужен и создал бы
 * второй экран. Android и web используют этот in-app оверлей: на Android системный Telecom
 * (ConnectionService) на части прошивок молча не рисует экран стороннего не-self-managed
 * phone account, поэтому оверлей — единственный надёжный UI входящего.
 */
function overlayEnabled(): boolean {
  return Platform.OS !== 'ios';
}

/**
 * Смонтирован ли и подписан ли оверлей входящего звонка.
 *
 * <p>Нужно тому, кто решает, рисовать ли полноэкранное уведомление о звонке: рисовать его
 * поверх видимого оверлея значит показать один звонок дважды — экраном и шторкой со своим
 * зацикленным рингтоном. Но и «приложение активно» само по себе не гарантия: если оверлей
 * почему-то не смонтирован, payload осядет в буфере и человек не увидит ничего. Уведомление
 * уступает дорогу только тогда, когда экран действительно есть кому показать.
 */
export function hasIncomingCallUiListener(): boolean {
  return listener != null;
}

export function subscribeIncomingCallUI(fn: Listener): () => void {
  listener = fn;
  if (pendingPayload) {
    const buffered = pendingPayload;
    pendingPayload = null;
    fn(buffered);
  }
  return () => {
    if (listener === fn) {
      listener = null;
    }
  };
}

/**
 * Triggers the React-tree incoming-call overlay.
 *
 * iOS: no-op — CallKit owns the incoming UI (see {@link overlayEnabled}).
 * Android / web: show the in-app overlay (Modal с Accept/Decline).
 */
export function presentIncomingCallUI(payload: IncomingCallUIPayload): void {
  if (!overlayEnabled()) return;
  // Групповые звонки выключены: чужой (старый) клиент всё ещё может позвонить,
  // но принимать такой вызов нам нечем — экран не показываем (стори 15.1).
  if (payload.callScope === 'group' && !GROUP_CALLS_ENABLED) return;
  /**
   * Линия занята — экран входящего не показываем.
   *
   * <p>Проверка стоит именно здесь, потому что это единственная воронка, через
   * которую входящий доходит до пользователя на Android и в вебе: WS-инвайт,
   * foreground-FCM и notifee — все зовут её. Без неё звонок из другого чата
   * рисовал полноэкранный экран поверх живого разговора, а рингтон уходил в ту
   * же аудиосессию, в которой шёл этот разговор.
   *
   * <p>Ответить звонящему «занято» отсюда нельзя — сокета у моста нет; это
   * делает WS-приёмник, который единственный и знает, что связь есть.
   */
  const busy = decideIncomingInviteWhileBusy(getActiveCall(), {
    chatId: payload.chatId,
    callInviteId: payload.inviteId ?? null,
    scope: payload.callScope,
  });
  if (busy.action !== 'ring') return;
  // Отсюда звонок действительно звонит — значит, его будет чем отменить, когда придёт
  // `accept` со второго нашего устройства или `bye` от передумавшего звонящего.
  noteIncomingCallRinging({
    chatId: payload.chatId,
    inviteId: payload.inviteId ?? null,
    scope: payload.callScope,
    callerUserId: payload.callerUserId ?? null,
  });
  /**
   * Тот же звонок вторым каналом (WS-инвайт и следом пуш, тап по шторке поверх уже открытого
   * экрана) — дополняем показанный, а не подменяем: у второго кадра бывает меньше полей
   * (нет аватара), и экран не должен их терять. Свёрнутый «Назад» звонок этим же вызовом
   * разворачивается обратно.
   */
  const prev = presentedPayload;
  presentedPayload = prev && isSameIncomingCall(prev, payload) ? { ...prev, ...payload } : payload;
  overlayHidden = false;
  if (listener) {
    listener(presentedPayload);
  } else {
    pendingPayload = presentedPayload;
  }
}

/** Показанный (или ждущий показа) входящий — чтобы отмена не сняла чужой звонок. */
let presentedPayload: IncomingCallUIPayload | null = null;
/** Входящий убран «Назад» в шторку — звонит молча, но ещё не отклонён. */
let overlayHidden = false;

function canonId(id: string): string {
  return id.trim().replace(/-/g, '').toLowerCase();
}

/** Один и тот же входящий: чат совпадает, а нога либо совпадает, либо у одной из копий неизвестна. */
export function isSameIncomingCall(a: IncomingCallUIPayload, b: IncomingCallUIPayload): boolean {
  if (canonId(a.chatId) !== canonId(b.chatId)) return false;
  return !a.inviteId || !b.inviteId || a.inviteId === b.inviteId;
}

/**
 * Убрать экран входящего, <b>не</b> отклоняя звонок (Android «Назад»).
 *
 * <p>Раньше «Назад» на экране входящего был «Отклонить» — то есть самый частый жест Android,
 * сделанный по привычке, клал трубку звонящему (аудит удобства 2026-10-04, §3 #27). Теперь
 * звонок остаётся: запись в реестре звонящих не трогаем, чтобы отмена со стороны звонящего и
 * таймаут по-прежнему его гасили, а ответить можно из шторки.
 *
 * @returns входящий, который убрали, — его покажет шторка
 */
export function hideIncomingCallUI(): IncomingCallUIPayload | null {
  if (!presentedPayload) return null;
  overlayHidden = true;
  pendingPayload = null;
  listener?.(null);
  return presentedPayload;
}

/** Входящий, убранный в шторку «Назад», — если такой сейчас есть. */
export function getHiddenIncomingCall(): IncomingCallUIPayload | null {
  return overlayHidden ? presentedPayload : null;
}

/**
 * «Ответить» нажали в шторке, когда экрана входящего ещё нет (приложение поднимается из фона
 * или с нуля). Экран входящего, получив этот звонок, отвечает сам — без второго нажатия.
 */
const ANSWER_INTENT_TTL_MS = 60_000;
let answerIntent: { chatId: string; until: number } | null = null;

export function markIncomingCallAnswerIntent(chatId: string): void {
  answerIntent = { chatId: canonId(chatId), until: Date.now() + ANSWER_INTENT_TTL_MS };
}

/** Забрать намерение ответить на звонок из этого чата: true — отвечать сразу. */
export function consumeIncomingCallAnswerIntent(chatId: string): boolean {
  const intent = answerIntent;
  if (!intent || intent.chatId !== canonId(chatId)) return false;
  answerIntent = null;
  return Date.now() <= intent.until;
}

/**
 * Снять экран входящего.
 *
 * @param chatId если задан, экран снимается только когда показан звонок <b>из этого</b>
 *     чата. Без фильтра отмена одного звонка гасила бы и второй, пришедший из другого
 *     чата, — а он-то как раз ещё звонит.
 */
export function dismissIncomingCallUI(chatId?: string): void {
  if (chatId && presentedPayload && presentedPayload.chatId !== chatId) {
    return;
  }
  // Запись о звонящем снимается и на iOS: там экрана оверлея нет, но реестр общий, и
  // зависшая запись потом гасила бы следующий звонок в том же чате.
  clearRingingIncomingCall(chatId ?? presentedPayload?.chatId ?? '');
  presentedPayload = null;
  overlayHidden = false;
  if (!overlayEnabled()) return;
  pendingPayload = null;
  listener?.(null);
}
